import React, {useState} from 'react';

import {v4} from 'uuid';

import {DashboardPage} from 'app/components/general/DashboardPage';
import TabbedModal from 'app/components/general/TabbedModal';
import {useCurrentUserState} from 'app/current-user/api';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useLocalStateFilters} from 'app/hooks';
import {
  convertKnowledgebaseLocationParamsToFetchParams,
  useKnowledgeBases,
} from 'app/kbs/api';
import {MessageExchange} from 'app/openai/components/TextEdition/MessagePanel';
import {KB_DASHBOARD_URL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';

import {CompactKBListItem} from './CompactKBListItem';
import {CreateKBButton} from './CreateKBButton';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {KBFetchParamsType, KBLocationParamsType} from 'app/kbs/types';
import type {WithRouterProps} from 'react-router';
import type {KnowledgebaseSerializedType} from 'sweet-types';

export function KnowledgebasesDashboard(routerProps: WithRouterProps) {
  const {is_super_manager} = useCurrentUserState().data!;

  const [showChat, setShowChat] = useState<number | null>(null);

  return (
    <DashboardPage<
      KnowledgebaseSerializedType,
      KBLocationParamsType,
      KBFetchParamsType
    >
      {...routerProps}
      title={gt('Knowledge Bases')}
      actionArea={
        is_super_manager ? (
          <CreateKBButton className="btn btn-sm btn-primary">
            <i className="fa fa-fw fa-plus-circle"></i>{' '}
            <span className="d-none d-md-inline">
              {gt('Create Knowledge base')}
            </span>
          </CreateKBButton>
        ) : null
      }
      useFiltersHook={useLocalStateFilters<
        KBFetchParamsType,
        KBLocationParamsType
      >(convertKnowledgebaseLocationParamsToFetchParams)}
      useDataHook={useKnowledgeBases}
      renderItem={(kb) => (
        <div key={kb.id}>
          <CompactKBListItem
            kb={kb}
            isSuperManager={is_super_manager}
            setShowChat={setShowChat}
          />
          <TabbedModal
            show={showChat === kb.id}
            onCancel={() => setShowChat(null)}
            className="mw-100 px-4"
            tabs={[
              {
                eventKey: 'kb-chat',
                name: gt(`Chat with ${kb.name}`),
                icon: 'fas fa-comment-dots',
                content: (
                  <MessageExchange
                    initialAIMessage={gt('How can I help you today?')}
                    threadId={v4()}
                    chatType={'rag'}
                    disabled={false}
                    object_id={kb.id}
                    content_type={'kb'}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
      messages={{
        counter: (count) =>
          ngt('%s knowledge base', '%s knowledge bases', count),
        notFound: (props, NoResultsMessage) => (
          <NoResultsMessage
            icon="far fa-book-open"
            head={gt('No Knowledge Bases were created yet')}
          >
            {is_super_manager ? (
              <div className="px-5">
                <p className="lead mx-md-5 px-sm-5">
                  {gt(
                    'Let your customers, suppliers and others access some of your procedures and policies in a beautiful knowledge base.'
                  )}
                </p>
                <CreateKBButton className="btn btn-primary btn-lg">
                  {gt('Create Knowledge Base')}
                </CreateKBButton>
              </div>
            ) : null}
          </NoResultsMessage>
        ),
      }}
      subscribeToPresence={{
        contentType: 'knowledgebase',
        objectsIds: (kbs) => kbs.map((kb) => kb.id),
      }}
    />
  );
}

const kbCt = getContentType('knowledgebase');

export const KBsDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={kbCt.icon}
    text={gt('Knowledge Bases')}
    link={KB_DASHBOARD_URL}
  />
);
